import { getMenuItems } from '../config/menuItems';
import { Button } from "./ui/button";
import { LogOut, Home, Moon, Sun, MoreHorizontal, FileText, Bell, HardDrive, Sparkles, Heart, Wind, Mail, Users } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import NotificationBell from "./NotificationBell";
import UserAvatar from "./UserAvatar";
import { useTheme } from "../lib/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const MORE_ACTIONS = [
  { icon: FileText, label: "News", to: "/news" },
  { icon: Bell, label: "Notifications", to: "/notifications" },
  { icon: HardDrive, label: "Storage", to: "/dashboard?storage=1" },
  { icon: Sparkles, label: "AI Insights", to: "/trends" },
  { icon: Heart, label: "Favorites", to: "/favorites" },
  { icon: Wind, label: "Pre-Game Routines", to: "/pre-routines" },
  { icon: Mail, label: "Weekly Report", to: "/dashboard?weeklyReport=1" },
  { icon: Users, label: "Get Support", to: "/messages?request_type=coach" },
];

export default function Navigation({ onLogout, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const menu = getMenuItems(user?.role);
  const { dark, toggle } = useTheme();

  return (
    <nav className="hidden md:block bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50">
      <div className="max-w-[1440px] mx-auto px-3 lg:px-6">
        <div className="flex items-center h-14 gap-1">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0 mr-1"
            onClick={() => navigate('/')}
            title="Go to Dashboard"
          >
            <Logo size="default" />
            <span
              className="hidden xl:block text-emerald-800 dark:text-emerald-400 tracking-wide whitespace-nowrap font-bold"
              style={{ fontFamily: "'Playfair Display SC', serif", fontSize: '12px', fontWeight: 800, letterSpacing: '0.05em' }}
              data-testid="elite-tagline"
            >
              Elite Mental Performance
            </span>
          </div>

          {/* Divider */}
          <div className="w-px h-7 bg-gray-200 dark:bg-gray-700 flex-shrink-0 mx-1" />

          {/* Dashboard */}
          <button
            data-testid="nav-dashboard"
            onClick={() => navigate('/dashboard')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
              location.pathname === '/dashboard'
                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-emerald-600'
            }`}
            title="Dashboard"
          >
            <Home className="w-4 h-4 flex-shrink-0" />
            <span className="hidden lg:inline">Dashboard</span>
          </button>

          {/* Main Navigation - Single row, no wrap */}
          <div className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto scrollbar-hide">
            {menu.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                    isActive
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}

            {/* More menu (3-dot) with extra actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-more-btn"
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-emerald-600 dark:hover:text-emerald-400"
                  title="More"
                >
                  <MoreHorizontal className="w-4 h-4 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="nav-more-menu">
                {MORE_ACTIONS.map((action) => (
                  <DropdownMenuItem
                    key={action.label}
                    data-testid={`nav-more-${action.label.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => navigate(action.to)}
                    className="cursor-pointer gap-2"
                  >
                    <action.icon className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm">{action.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right actions */}
          <div className="flex-shrink-0 flex items-center gap-1">
            <NotificationBell />
            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={dark ? "Light mode" : "Dark mode"}
            >
              {dark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-gray-500" />}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center gap-1.5 px-1.5 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              data-testid="nav-settings-btn"
            >
              <UserAvatar userId={user?.id} size="xs" />
              <span className="text-xs text-gray-600 dark:text-gray-400 font-medium hidden xl:block">{user?.name?.split(' ')[0] || 'Settings'}</span>
            </button>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="flex items-center h-8 px-2 gap-1"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="text-xs hidden lg:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}
